// client/src/hooks/useDevices.js
// CRUD hooks for the devices nested resource + the rotate-code action.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

const listKey = (campId) => ['camps', campId, 'devices'];

export function useDevices(campId) {
  return useQuery({
    queryKey: listKey(campId),
    queryFn: () => api.get(`/api/camps/${campId}/devices`).then((r) => r.devices),
    enabled: !!campId,
    // Refetch while the tab is open so kiosk connection status stays current.
    refetchInterval: 60000,
  });
}

function invalidate(qc, campId) {
  qc.invalidateQueries({ queryKey: listKey(campId) });
}

export function useCreateDevice(campId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/api/camps/${campId}/devices`, body),
    onSuccess: () => invalidate(qc, campId),
  });
}

export function useUpdateDevice(campId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, patch }) =>
      api.patch(`/api/camps/${campId}/devices/${deviceId}`, patch),
    onSuccess: () => invalidate(qc, campId),
  });
}

export function useDeleteDevice(campId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deviceId) => api.delete(`/api/camps/${campId}/devices/${deviceId}`),
    onSuccess: () => invalidate(qc, campId),
  });
}

export function useRotateDeviceCode(campId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deviceId) =>
      api.post(`/api/camps/${campId}/devices/${deviceId}/rotate-code`),
    onSuccess: () => invalidate(qc, campId),
  });
}

/**
 * Move a device to another camp with all its settings. Server may regenerate
 * the 6-digit code on collision — response is { device, codeChanged }.
 */
export function useMoveDevice(campId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, targetCampId }) =>
      api.post(`/api/camps/${campId}/devices/${deviceId}/move`, { targetCampId }),
    onSuccess: (_data, vars) => {
      invalidate(qc, campId);
      invalidate(qc, vars.targetCampId);
    },
  });
}

/**
 * Kiosk screensaver upload (ADR-019). Raw fetch because it's multipart —
 * same shape as uploadCampLogo. The blob is the CropModal's pre-cropped
 * portrait 1200×1920 JPEG, never the manager's original file.
 */
async function uploadScreensaver(campId, deviceId, blob) {
  const fd = new FormData();
  fd.append('screensaver', blob, 'screensaver.jpg');
  const res = await fetch(`/api/camps/${campId}/devices/${deviceId}/screensaver`, {
    method: 'PUT',
    body: fd,
    credentials: 'same-origin',
  });
  const text = await res.text();
  let payload = null;
  try { payload = text ? JSON.parse(text) : null; } catch { /* leave null */ }
  if (!res.ok) {
    const err = new Error(payload?.message || payload?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = payload?.error;
    throw err;
  }
  return payload;
}

export function useUploadDeviceScreensaver(campId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, blob }) => uploadScreensaver(campId, deviceId, blob),
    onSuccess: () => invalidate(qc, campId),
  });
}

export function useDeleteDeviceScreensaver(campId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deviceId) =>
      api.delete(`/api/camps/${campId}/devices/${deviceId}/screensaver`),
    onSuccess: () => invalidate(qc, campId),
  });
}
